import { useState } from 'react';
import type { FormEvent } from 'react';
import { useAccountSession } from '../../auth/AccountSessionProvider';
import { useI18n } from '../../i18n/useI18n';
import { toApiError } from '../../api/errors';
import type { ApiError } from '../../api/errors';
import { PASSWORD_MIN_LENGTH } from '../../api/types';
import { Alert, ErrorAlert } from '../../ui/Alert';
import { Button, Card, PageHead } from '../../ui/primitives';
import { TextField } from '../../ui/form';

/**
 * Changing one's own password, and nobody else's.
 *
 * The current one has to be shown even though the caller is already signed in:
 * a stolen session must not be enough to keep the account. Everything that
 * follows is on screen before the button, because all of it is discovered
 * afterwards otherwise — every session of this account closes, including the
 * one being used, so the next thing to happen is the sign-in screen.
 *
 * There is no reset here and no route that sets somebody else's password. Both
 * absences are the product's, not an omission of this screen's: this product
 * has no mail, and a password is set from the command line by somebody who is
 * already on the host.
 */
export function PasswordScreen() {
  const { t } = useI18n();
  const { session, changePassword } = useAccountSession();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [errors, setErrors] = useState<{ current?: string; next?: string; repeat?: string }>({});
  const [failure, setFailure] = useState<ApiError | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();

    const found: { current?: string; next?: string; repeat?: string } = {};
    if (current === '') found.current = t('password.check.currentRequired');
    if (next.length < PASSWORD_MIN_LENGTH) {
      found.next = t('password.check.tooShort', { least: PASSWORD_MIN_LENGTH });
    } else if (next === current) {
      found.next = t('password.check.sameAsCurrent');
    }
    if (repeat !== next) found.repeat = t('password.check.mismatch');

    setErrors(found);
    setFailure(null);
    if (Object.keys(found).length > 0) return;

    setSubmitting(true);
    try {
      await changePassword({ current, next });
      // On success this component is gone: the session closed with the change.
    } catch (error: unknown) {
      const failed = toApiError(error);
      // The server names the box to go back to; it is never guessed from a code.
      const onNext = failed.fieldError('next');
      const onCurrent = failed.fieldError('password');
      if (onNext !== undefined) setErrors({ next: t('password.check.refused') });
      else if (onCurrent !== undefined && failed.code === 'bad_credentials') {
        setErrors({ current: t('password.check.currentWrong') });
      } else setFailure(failed);
      setSubmitting(false);
    }
  }

  return (
    <div className="content-inner">
      <PageHead title={t('password.title')} />

      <div className="stack">
        {failure !== null && <ErrorAlert error={failure} />}

        <Card>
          <div className="card-head">
            <span className="ct">{t('password.section')}</span>
          </div>
          <div className="card-pad">
            {session !== null && (
              <p className="hint" style={{ marginTop: 0 }}>
                {t('password.forAccount', { email: session.actor.email })}
              </p>
            )}

            <form onSubmit={(event) => void onSubmit(event)} noValidate>
              <TextField
                label={t('password.current.label')}
                hint={t('password.current.hint')}
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(event) => {
                  setCurrent(event.target.value);
                }}
                {...(errors.current === undefined ? {} : { error: errors.current })}
              />
              <TextField
                label={t('password.next.label')}
                hint={t('password.next.hint', { least: PASSWORD_MIN_LENGTH })}
                type="password"
                autoComplete="new-password"
                value={next}
                onChange={(event) => {
                  setNext(event.target.value);
                }}
                {...(errors.next === undefined ? {} : { error: errors.next })}
              />
              <TextField
                label={t('password.repeat.label')}
                type="password"
                autoComplete="new-password"
                value={repeat}
                onChange={(event) => {
                  setRepeat(event.target.value);
                }}
                {...(errors.repeat === undefined ? {} : { error: errors.repeat })}
              />

              <Button type="submit" variant="primary" disabled={submitting}>
                {submitting ? `${t('password.submitting')}…` : t('password.submit')}
              </Button>
            </form>
          </div>
        </Card>

        <Alert tone="warn" title={t('password.effect.title')}>
          <p>{t('password.effect.sessions')}</p>
          <p style={{ marginTop: 8 }}>{t('password.effect.noReset')}</p>
        </Alert>
      </div>
    </div>
  );
}
